import NextLink from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { Collapse, LinkBox, LinkOverlay, Text, useMediaQuery, VStack } from '@chakra-ui/react';

import { breakpoints } from 'app/themeConfig';
import { useWindowResize } from 'shared/hooks';
import ChevronIcon from 'shared/ChevronIcon';
import Tag from 'shared/Tag';

import { HEADER_BREAKPOINT } from 'constants/global';

export const TabCard = ({
  link,
  src,
  tag = null,
  title,
  text,
  updateHeaderMenuState,
  ...props
}) => {
  const cardRef = useRef(null);
  const [isTextVisible, setIsTextVisible] = useState(true);
  const [isCompactDesignActive, setIsCompactDesignActive] = useState(false);
  const { width, height } = useWindowResize();
  const [isLaptop, isXxl, isXxxl, isXds] = useMediaQuery([
    `(min-width: ${breakpoints.xxl})`,
    `(min-width: ${breakpoints.xxl}) and (max-width: ${breakpoints.xxxl})`,
    `(min-width: ${breakpoints.xxxl}) and (max-width: ${breakpoints.xds})`,
    `(min-width: ${breakpoints.xds})`,
  ]);

  useEffect(() => {
    if (cardRef.current) {
      const cardHeight = cardRef.current.offsetHeight;

      if (
        (isXxl && cardHeight < 230) ||
        (isXxxl && cardHeight < 185) ||
        (isXds && cardHeight < 225)
      ) {
        setIsCompactDesignActive(true);
        setIsTextVisible(false);
      } else {
        setIsCompactDesignActive(false);
        setIsTextVisible(true);
      }
    }
  }, [width, height, isXxl, isXxxl, isXds]);

  const isHttp = link?.startsWith('http');

  return (
    <LinkBox as="article" ref={cardRef} {...props}>
      <LinkOverlay
        as={isHttp ? 'a' : NextLink}
        href={link}
        isExternal={isHttp}
        onClick={() => updateHeaderMenuState('close')}
      >
        <VStack
          role="group"
          pos="relative"
          w="full"
          h={{
            mobile: 'full',
            [HEADER_BREAKPOINT]: 'clamp(175px, 25vh, 240px)',
            xds: 'clamp(180px, 25vh, 240px)',
          }}
          p={{
            mobile: '16px 16px 16px 72px',
            xxm: '0 20px 20px 20px',
            [HEADER_BREAKPOINT]: '0 24px 24px 24px',
            xds: '0 32px 32px 32px',
          }}
          borderRadius={{ mobile: '20px', [HEADER_BREAKPOINT]: '40px' }}
          justify="end"
          bgColor="bg.grayLight"
          transition="all .2s"
          _hover={{
            bgColor: { [HEADER_BREAKPOINT]: 'bg.accent' },
          }}
          onMouseEnter={() => isCompactDesignActive && isLaptop && setIsTextVisible(true)}
          onMouseLeave={() => isCompactDesignActive && isLaptop && setIsTextVisible(false)}
        >
          <ChevronIcon
            src={src}
            pos="absolute"
            top={{ mobile: '16px', xxm: '8px', [HEADER_BREAKPOINT]: '12px' }}
            left={{ mobile: '16px', xxm: '8px', [HEADER_BREAKPOINT]: '12px' }}
            boxSize={{ mobile: '40px', xxm: '52px', [HEADER_BREAKPOINT]: '60px' }}
            p={{ mobile: '10px', xxm: '13px', [HEADER_BREAKPOINT]: '15px' }}
            boxSizing="border-box"
            bgColor="bg.white"
            transition="all .2s"
            _groupHover={isCompactDesignActive && { opacity: 0 }}
            imageProps={{
              boxSize: { mobile: '20px', xxm: '26px', [HEADER_BREAKPOINT]: '30px' },
            }}
          />

          {tag && (
            <Tag
              p="8px 12px"
              textStyle="h4"
              fontSize={{ mobile: '12px', [HEADER_BREAKPOINT]: '16px' }}
              lineHeight={{ mobile: '110%', [HEADER_BREAKPOINT]: '140%' }}
              pos="absolute"
              top="12px"
              right="12px"
              hideBelow="xxm"
            >
              {tag}
            </Tag>
          )}

          <VStack w="full" align="start" gap="0" fontWeight="500">
            <Text
              as="h3"
              textStyle="h1"
              fontWeight="500"
              fontSize={{ mobile: '16px', [HEADER_BREAKPOINT]: '20px', xds: '22px' }}
            >
              {title}

              {tag && (
                <Text as="span" textStyle="h3" color="text.darkGray" hideFrom="xxm">
                  {` (${tag})`}
                </Text>
              )}
            </Text>

            {text && (
              <Collapse in={isTextVisible} unmountOnExit>
                <Text
                  as="p"
                  textStyle="h3"
                  fontSize={{ mobile: '12px', [HEADER_BREAKPOINT]: '16px' }}
                  pt={{ mobile: '4px', xxm: '12px', [HEADER_BREAKPOINT]: '8px' }}
                >
                  {text}
                </Text>
              </Collapse>
            )}
          </VStack>
        </VStack>
      </LinkOverlay>
    </LinkBox>
  );
};
